/**
 * blackHolePipeline — black hole with gravitational lensing.
 *
 * Raymarched sphere with high-density at the center. Light rays
 * passing near the black hole are bent by the gravity field,
 * producing the characteristic Einstein ring around the event
 * horizon. An accretion disk surrounds the black hole, with
 * Doppler-shifted color bands.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface BlackHolePipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float valueNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 4; i++) {
    v += a * valueNoise(p);
    p = p * 2.0 + vec2(1.7, 9.2);
    a *= 0.5;
  }
  return v;
}

// SDF for an oblate spheroid (black hole).
float sdBlackHole(vec3 p) {
  // Oblate (disk-shaped).
  vec3 q = vec3(p.x, p.y * 2.5, p.z);
  return length(q) - 0.18;
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  // Aspect-corrected screen coords.
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);

  // Background: starfield with subtle nebula.
  float bgNoise = hash21(floor(gl_FragCoord.xy / 3.0));
  float bgStar = step(0.998, bgNoise);
  vec3 bgColor = vec3(0.02, 0.03, 0.06);
  bgColor += vec3(0.85, 0.9, 1.0) * bgStar * 0.4;
  // Add a soft nebula.
  bgColor += vec3(0.10, 0.05, 0.20) * fbm(p * 2.0) * 0.3;

  // Gravitational lensing: bend rays based on distance to black hole.
  // Strong field near the center, falls off with 1/r.
  float distToCenter = length(p);
  // Vector from pixel to center.
  vec2 toCenter = -p / max(distToCenter, 0.001);
  // Bending strength.
  float bendStrength = 0.15 / max(distToCenter * distToCenter, 0.005);
  // Bended ray direction (in 2D for simplicity).
  vec2 bended = p + toCenter * bendStrength;
  // Distance from bended ray to the center.
  float bendedDist = length(bended);

  // Color: if the bended ray points at the disk (in the equatorial plane),
  // we see the disk. Otherwise we see the background.
  // Equatorial plane: |p.y| < diskHeight.
  float diskHeight = 0.08 + bass * 0.04;
  float inDisk = smoothstep(diskHeight + 0.02, diskHeight - 0.02, abs(p.y));

  // Disk color: hot orange/yellow at the center, cooler at the edge.
  // Use the bended radius to color the disk (Doppler-like effect).
  vec3 hotColor = vec3(1.0, 0.95, 0.50);
  vec3 midColor = vec3(1.0, 0.55, 0.10);
  vec3 coolColor = vec3(0.95, 0.30, 0.45);
  float diskHot = smoothstep(0.6, 0.2, bendedDist);
  float diskMid = smoothstep(0.2, 0.4, bendedDist);
  vec3 diskColor = mix(coolColor, midColor, diskMid);
  diskColor = mix(diskColor, hotColor, diskHot);
  // Audio: shift the disk color.
  diskColor *= 1.0 + high * 0.4;
  // Add some noise to the disk for texture.
  diskColor *= 0.8 + 0.4 * fbm(p * 20.0 + vec2(uTime * 2.0, 0.0));
  // Slow rotation of disk.
  float diskRot = atan(p.y, p.x) + uTime * 0.5;
  diskColor *= 0.8 + 0.4 * sin(diskRot * 5.0);

  // Black hole event horizon (the central dark sphere).
  float eventHorizon = smoothstep(0.20, 0.18, distToCenter);

  // Einstein ring: bright ring at the photon sphere.
  float photonSphere = exp(-pow(distToCenter - 0.32, 2.0) * 200.0);

  // Combine.
  vec3 col = bgColor;
  col = mix(col, diskColor, inDisk);
  col = mix(col, vec3(1.0, 0.7, 0.3), photonSphere * 1.5);
  col = mix(col, vec3(0.0, 0.0, 0.0), eventHorizon);

  // Vignette.
  float vig = 1.0 - dot(p, p) * 0.2;
  col *= clamp(vig, 0.0, 1.0);

  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}
`;

export function computeBlackHoleBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.4);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.6 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 0.9 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.1 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.3 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.6 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.0 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.4 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 2.9 + 3.0);
}

export function computeBlackHoleRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createBlackHolePipeline(gl: WebGL2RenderingContext): BlackHolePipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawBlackHole(
  gl: WebGL2RenderingContext,
  pipe: BlackHolePipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
